/*
  Al Tauheed Textiles: inline variant picker on product cards (<tt-card-variants>).
  Resolves the Shopify variant from the selected option values and keeps everything on the card
  pointing at that ONE variant: hidden variant id, price, image (variant.featured_media, else the
  product's main image), availability and the Add to cart button.
  Submitting is left to the theme's <product-form> (cart drawer, count, totals, errors).
  Variant data comes from the card's JSON (rendered by snippets/tt-card-variant-form.liquid).
*/
(function () {
  if (customElements.get('tt-card-variants')) return;

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  class TTCardVariants extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      var dataEl = this.querySelector('script[data-tt-variants]');
      if (!dataEl) return;
      try {
        var data = JSON.parse(dataEl.textContent);
        this.variants = data.variants || [];
        this.strings = data.strings || {};
        this.media = data.media || {};
      } catch (e) {
        return; /* button stays disabled: nothing can be added with the wrong variant */
      }
      this.initialized = true;
      this.form = this.querySelector('form');
      this.idInput = this.form.querySelector('input[name="id"]');
      this.button = this.form.querySelector('button[type="submit"]');
      this.buttonText = this.button.querySelector('span');
      this.soldOutMessage = this.button.querySelector('.sold-out-message');
      this.groups = Array.prototype.slice.call(this.querySelectorAll('[data-option-index]')).filter(function (el) {
        return el.tagName !== 'SELECT';
      });
      var card = this.closest('.card-wrapper') || this.parentElement;
      this.priceEl = card ? card.querySelector('.card-information .price') : null;

      /* Card image: the first (main) image of the card; remember it as the fallback */
      this.mediaWrap = card ? card.querySelector('.card__media .media') : null;
      this.image = this.mediaWrap ? this.mediaWrap.querySelector('img') : null;
      if (this.image) {
        this.original = {
          src: this.image.getAttribute('src'),
          srcset: this.image.getAttribute('srcset'),
          alt: this.image.getAttribute('alt')
        };
      }
      this.imageRequest = 0;

      this.addEventListener('change', this.update.bind(this));
      this.update();
    }

    selectedValues() {
      return this.groups.map(function (group) {
        var select = group.querySelector('select');
        if (select) return select.value;
        var checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(values) {
      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        var match = true;
        for (var j = 0; j < values.length; j++) {
          if (v.options[j] !== values[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    /* A value is available if some available variant has it together with the other current selections. */
    isValueAvailable(index, value, values) {
      return this.variants.some(function (v) {
        if (!v.available || v.options[index] !== value) return false;
        for (var j = 0; j < values.length; j++) {
          if (j !== index && v.options[j] !== values[j]) return false;
        }
        return true;
      });
    }

    markAvailability(values) {
      var self = this;
      this.groups.forEach(function (group, index) {
        var select = group.querySelector('select');
        if (select) {
          Array.prototype.forEach.call(select.options, function (opt) {
            var ok = self.isValueAvailable(index, opt.value, values);
            var label = opt.getAttribute('data-label') || opt.value;
            opt.textContent = ok ? label : label + ' – ' + (self.strings.soldOut || 'Sold out');
            opt.toggleAttribute('data-unavailable', !ok);
          });
          return;
        }
        Array.prototype.forEach.call(group.querySelectorAll('input[type="radio"]'), function (radio) {
          var ok = self.isValueAvailable(index, radio.value, values);
          var label = group.querySelector('label[for="' + radio.id + '"]');
          radio.toggleAttribute('data-unavailable', !ok);
          if (label) {
            label.classList.toggle('is-unavailable', !ok);
            var note = label.querySelector('.tt-card-variants__unavailable');
            if (note) note.hidden = ok;
          }
        });
      });
    }

    renderPrice(variant) {
      if (!this.priceEl || !variant) return;
      var s = this.strings;
      var onSale = !!variant.compare;
      var html =
        '<div class="price__container">' +
          '<div class="price__regular">' +
            '<span class="visually-hidden visually-hidden--inline">' + escapeHtml(s.regularPrice || '') + '</span>' +
            '<span class="price-item price-item--regular">' + escapeHtml(variant.price) + '</span>' +
          '</div>' +
          '<div class="price__sale">' +
            (onSale
              ? '<span class="visually-hidden visually-hidden--inline">' + escapeHtml(s.regularPrice || '') + '</span>' +
                '<span><s class="price-item price-item--regular">' + escapeHtml(variant.compare) + '</s></span>'
              : '') +
            '<span class="visually-hidden visually-hidden--inline">' + escapeHtml(s.salePrice || '') + '</span>' +
            '<span class="price-item price-item--sale price-item--last">' + escapeHtml(variant.price) + '</span>' +
          '</div>' +
        '</div>';
      this.priceEl.innerHTML = html;
      this.priceEl.classList.remove('price--no-compare');
      this.priceEl.classList.toggle('price--on-sale', onSale);
      this.priceEl.classList.toggle('price--sold-out', !variant.available);
    }

    renderButton(variant) {
      var productForm = this.querySelector('product-form');
      if (productForm) {
        productForm.error = false;
        if (typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage();
      }
      this.button.removeAttribute('aria-disabled');
      if (this.soldOutMessage) this.soldOutMessage.classList.add('hidden');
      this.buttonText.classList.remove('hidden');

      var s = this.strings;
      if (!variant) {
        this.button.disabled = true;
        this.buttonText.textContent = s.unavailable || 'Unavailable';
      } else if (!variant.available) {
        this.button.disabled = true;
        this.buttonText.textContent = s.soldOut || 'Sold out';
      } else {
        this.button.disabled = false;
        this.buttonText.textContent = s.addToCart || 'Add to cart';
      }
    }

    /* Show the resolved variant's own image (Shopify variant.featured_media); otherwise the product's main image */
    renderImage(variant) {
      if (!this.image || !this.original) return;
      var media = variant && variant.m ? this.media[variant.m] : null;
      var target = media
        ? { src: media.src, srcset: media.srcset, alt: media.alt }
        : this.original;
      var img = this.image;
      if (this.mediaWrap) this.mediaWrap.classList.toggle('tt-variant-image', !!media);
      if (img.getAttribute('src') === target.src && (img.getAttribute('srcset') || '') === (target.srcset || '')) return;
      /* Swap in the same step as price / id / button, so every part of the card always shows the same
         variant. The browser keeps painting the previous image until the new file has loaded (no blank
         frame), and the frame keeps its ratio (no layout shift). */
      this.imageRequest++;
      if (target.srcset) img.setAttribute('srcset', target.srcset); else img.removeAttribute('srcset');
      img.setAttribute('src', target.src);
      if (target.alt != null) img.setAttribute('alt', target.alt);
    }

    update() {
      var values = this.selectedValues();
      var variant = this.findVariant(values);
      this.markAvailability(values);
      if (variant) this.idInput.value = variant.id;
      this.currentVariant = variant;
      this.setAttribute('data-variant-id', variant ? variant.id : '');
      this.renderPrice(variant);
      this.renderImage(variant);
      this.renderButton(variant);
    }
  }

  customElements.define('tt-card-variants', TTCardVariants);
})();
